import React from 'react';
import { Outlet } from 'react-router-dom';
import { Container, Paper, Box, Typography, IconButton } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTheme } from '../../contexts/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

// Define subtle pulsing animation for dark mode
const pulseGlowDark = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(165, 205, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(165, 205, 255, 0.5);
  }
  100% {
    box-shadow: 0 0 10px rgba(165, 205, 255, 0.3);
  }
`;

// Define subtle pulsing animation for light mode
const pulseGlowLight = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(148, 194, 253, 0.2);
  }
  50% {
    box-shadow: 0 0 15px rgba(148, 194, 253, 0.4);
  }
  100% {
    box-shadow: 0 0 10px rgba(148, 194, 253, 0.2);
  }
`;

// Glowing light animation for dark mode
const glowSweepDark = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 400px 0;
  }
`;

// Glowing light animation for light mode
const glowSweepLight = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 400px 0;
  }
`;

const AuthLayout = () => {
  const { themeMode, toggleTheme } = useTheme();

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      position: 'relative', 
      overflow: 'hidden'
    }}>
      {/* Background gradient */}
      <Box 
        sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          zIndex: -1,
          background: theme => theme.palette.mode === 'light' 
            ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' 
            : 'linear-gradient(135deg, #2c3e50 0%, #1a1a2e 100%)',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            position: 'absolute', 
            top: '10%', 
            left: '5%', 
            width: '25vw', 
            height: '25vw', 
            borderRadius: '50%', 
            background: 'rgba(255, 10, 120, 0.1)',
            filter: 'blur(40px)'
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: '15%', 
            right: '10%', 
            width: '20vw', 
            height: '20vw', 
            borderRadius: '50%', 
            background: 'rgba(0, 150, 255, 0.1)',
            filter: 'blur(40px)'
          }} 
        />
      </Box>
      
      {/* Theme toggle button */}
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
        <IconButton 
          onClick={toggleTheme} 
          sx={{ 
            bgcolor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            '&:hover': {
              bgcolor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          {themeMode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
        </IconButton>
      </Box>
      
      {/* Left section - 2/3 width */}
      <Box sx={{ 
        width: '67%', 
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 6,
        position: 'relative'
      }}>
        {/* Feature content */}
        <Box sx={{ 
          maxWidth: '700px',
          textAlign: 'left',
          position: 'relative',
          zIndex: 2,
          px: { xs: 4, md: 8 },
          py: { xs: 6, md: 8 },
          backdropFilter: 'blur(8px)',
          backgroundColor: themeMode === 'dark' 
            ? 'rgba(31, 33, 51, 0.6)' 
            : 'rgba(255, 255, 255, 0.5)',
          borderRadius: 4,
          border: themeMode === 'dark'
            ? '1px solid rgba(165, 205, 255, 0.2)'
            : '1px solid rgba(148, 194, 253, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800, 
              mb: 6,
              textAlign: 'center',
              background: themeMode === 'dark' 
                ? 'linear-gradient(90deg, #a5cdff, #ffc3ee, #c4f5e5)' 
                : 'linear-gradient(90deg, #4285F4, #df8fc9, #b5ead7)',
              backgroundSize: '200% auto',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'gradient 5s ease infinite',
              '@keyframes gradient': {
                '0%': { backgroundPosition: '0% center' },
                '50%': { backgroundPosition: '100% center' },
                '100%': { backgroundPosition: '0% center' },
              },
              textShadow: themeMode === 'dark'
                ? '0 0 20px rgba(165, 205, 255, 0.5)'
                : '0 0 20px rgba(148, 194, 253, 0.5)',
              fontSize: { xs: '2.2rem', md: '2.8rem' },
            }}
          >
            Transforming Credit Monitoring
          </Typography>
          
          <Box sx={{ mb: 6 }}>
            {[
              'Upload and manage your customer dues',
              'Flag Defaulters and Blacklisted customers',
              'Gain visibility into shared customer behavior',
              'Approve or monitor status changes within your network',
              'Analyze recovery trends across associations'
            ].map((text, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3.5,
                  opacity: 0.97,
                  transform: 'translateX(0)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    transform: 'translateX(12px) scale(1.03)',
                    opacity: 1
                  }
                }}
              >
                <Box 
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 3,
                    background: themeMode === 'dark' 
                      ? 'linear-gradient(135deg, #94c2fd, #6a9eda)' 
                      : 'linear-gradient(135deg, #bcd6fe, #94c2fd)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    transform: 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'rotate(360deg)'
                    }
                  }}
                >
                  <Typography 
                    variant="body1" 
                    component="span" 
                    sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '18px', 
                      color: themeMode === 'dark' ? '#fff' : '#333' 
                    }}
                  >
                    ✓
                  </Typography>
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '1.2rem',
                    color: themeMode === 'dark' ? '#e8e8fd' : '#3a3f4b',
                    textShadow: themeMode === 'dark'
                      ? '0 0 10px rgba(232, 232, 253, 0.2)'
                      : 'none',
                  }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 6, 
              fontWeight: 800,
              fontSize: '1.5rem',
              letterSpacing: '1px',
              textAlign: 'center',
              color: themeMode === 'dark' ? '#a5cdff' : '#4285F4',
              textShadow: themeMode === 'dark'
                ? '0 0 15px rgba(165, 205, 255, 0.5)'
                : '0 0 15px rgba(148, 194, 253, 0.5)',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '25%',
                width: '50%',
                height: 3,
                borderRadius: 2,
                background: themeMode === 'dark'
                  ? 'linear-gradient(90deg, transparent, #a5cdff, transparent)'
                  : 'linear-gradient(90deg, transparent, #4285F4, transparent)',
              }
            }}
          >
            Secure. Transparent. Association-Driven.
          </Typography>
        </Box>
      </Box>
      
      {/* Right section - 1/3 width */}
      <Box sx={{ 
        width: { xs: '100%', md: '33%' }, 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 2, sm: 4 }
      }}>
        <Paper 
          elevation={3}
          sx={{
            p: 4,
            pt: 8,
            width: '100%',
            maxWidth: '450px',
            borderRadius: 2,
            animation: themeMode === 'dark' 
              ? `${pulseGlowDark} 4s ease-in-out infinite` 
              : `${pulseGlowLight} 4s ease-in-out infinite`,
            border: themeMode === 'light' 
              ? '1px solid rgba(148, 194, 253, 0.15)' 
              : '1px solid rgba(165, 205, 255, 0.2)',
            background: theme => theme.palette.mode === 'light' 
              ? 'white' 
              : theme.palette.background.paper,
            position: 'relative'
          }}
        >
          {/* Logo on top of login form */}
          <Box 
            sx={{ 
              position: 'absolute',
              top: -45,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 5
            }}
          >
            <Box 
              sx={{ 
                backgroundColor: themeMode === 'light' ? 'white' : '#353853', 
                borderRadius: '12px', 
                p: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: themeMode === 'light' ? '1px solid rgba(148, 194, 253, 0.2)' : '1px solid rgba(165, 205, 255, 0.3)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 220,
                height: 90,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: themeMode === 'light'
                    ? 'linear-gradient(90deg, transparent, rgba(148, 194, 253, 0.2), transparent)'
                    : 'linear-gradient(90deg, transparent, rgba(165, 205, 255, 0.3), transparent)',
                  backgroundSize: '600px 100%',
                  animation: themeMode === 'light'
                    ? `${glowSweepLight} 4s linear infinite`
                    : `${glowSweepDark} 4s linear infinite`,
                  zIndex: 0
                },
              }}
            >
              <img 
                src={themeMode === 'dark' 
                  ? require('../../assets/Full LogoDM.png') 
                  : require('../../assets/FullLogoLM.png')} 
                alt="Unity Logo" 
                style={{ 
                  width: '100%', 
                  maxHeight: 70,
                  position: 'relative',
                  zIndex: 1
                }} 
              />
            </Box>
          </Box>
          
          <Outlet />
        </Paper>
      </Box>
    </Box>
  );
};

export default AuthLayout;